import { useRef, useState, type ReactElement, type Ref } from 'react';
import {
  Button,
  DialogTrigger,
  Link,
  Menu,
  MenuItem,
  Popover,
  type Key,
} from 'react-aria-components';
import { Link as RouterLink, NavLink, useLocation } from 'react-router';
import { logos } from '../assets';
import { FeedbackButton } from '../feedback/FeedbackButton';
import { ButtonLink } from '../ui/Button';
import { cx } from '../ui/cx';
import { EmptyState } from '../ui/EmptyState';
import { Icon } from '../ui/Icon';
import { StatusPill } from '../ui/StatusPill';
import { initialsOf, visibleLevelOne } from './levelOne';
import type { AvatarTone, BellState, LevelOneId, Me } from './types';
import s from './AppHeader.module.css';

const AVATAR_TONE: Record<AvatarTone, string | undefined> = {
  gold: s.toneGold,
  petrol: s.tonePetrol,
  cyan: s.toneCyan,
  sky: s.toneSky,
  navy: s.toneNavy,
};

export interface AppHeaderProps {
  me: Me;
  /** Level 1 items this person can use. Shown in the fixed order; the others are hidden. */
  nav: readonly LevelOneId[];
  /** Override the default route of a Level 1 item. */
  navHrefs?: Partial<Record<LevelOneId, string>>;
  /**
   * The Level 1 item the page belongs to (QA-045): "page" on its own address, "true" on a page
   * under it (the queue under Home). Undefined: the item whose address the page is under.
   */
  current?: LevelOneId | null;
  /** The bell: unread notices and getting-started steps. */
  bell: BellState;
  /** Shows "Preview as" in the avatar menu (granted in Settings › System administration › Preview & testing). */
  canPreview: boolean;
  helpOpen: boolean;
  onHelpToggle: () => void;
  /** Opens the Help panel (from the avatar menu). */
  onHelpOpen: () => void;
  searchOpen: boolean;
  onSearchOpen: () => void;
  onSignOut: () => void;
  settingsHref?: string;
  /**
   * Opens Preview as (TEST1). The menu item shows only with `canPreview` and this; focus returns
   * to the account menu button when the panel closes.
   */
  onPreviewOpen?: (returnFocusTo: HTMLElement | null) => void;
  homeHref?: string;
  /**
   * Opens Send feedback (APP-104 §3.1); `returnFocusTo` is the control that opened it. Left out,
   * the header's button and the account menu's entry are hidden (the setting is off, or the
   * person isn't in its audience).
   */
  onFeedbackOpen?: (returnFocusTo: HTMLElement | null) => void;
  /** Lets the shell return focus to the Help button when the panel closes. */
  helpButtonRef?: Ref<HTMLButtonElement>;
}

/** The navy header: logo, Level 1, Search & jump, Help, bell and the avatar menu. */
export function AppHeader({
  me,
  nav,
  navHrefs,
  current,
  bell,
  canPreview,
  helpOpen,
  onHelpToggle,
  onHelpOpen,
  searchOpen,
  onSearchOpen,
  onSignOut,
  settingsHref = '/settings/me',
  onPreviewOpen,
  homeHref = '/',
  onFeedbackOpen,
  helpButtonRef,
}: AppHeaderProps): ReactElement {
  const items = visibleLevelOne(nav, navHrefs);
  const { pathname } = useLocation();
  const feedbackRef = useRef<HTMLButtonElement>(null);
  return (
    <header className={s.header}>
      <div className={s.row}>
        <Link href={homeHref} className={s.logoLink}>
          <img className={s.logo} src={logos.horizontalWhite} alt="Areté" width={127} height={34} />
        </Link>
        <nav aria-label="Main" className={s.nav}>
          <ul className={s.navList}>
            {items.map((item) => (
              <li key={item.id}>
                {current === undefined ? (
                  <NavLink
                    to={item.href}
                    end={item.id === 'home'}
                    className={s.navLink}
                    data-testid={`nav-${item.id}`}
                  >
                    {item.label}
                  </NavLink>
                ) : (
                  <RouterLink
                    to={item.href}
                    className={s.navLink}
                    data-testid={`nav-${item.id}`}
                    {...(item.id === current && {
                      'aria-current': pathname === item.href ? 'page' : 'true',
                    })}
                  >
                    {item.label}
                  </RouterLink>
                )}
              </li>
            ))}
          </ul>
        </nav>
        <div className={s.cluster} role="group" aria-label="Search, help and account">
          <Button
            className={cx(s.clusterButton, s.search)}
            onPress={onSearchOpen}
            aria-haspopup="dialog"
            aria-expanded={searchOpen}
            aria-keyshortcuts="Control+K Meta+K"
          >
            <Icon name="search" size={16} />
            {/* The words stay in the accessible name even when narrow windows hide them. */}
            <span className={s.searchLabel}>
              Search or jump to<span aria-hidden="true">…</span>
            </span>
            <kbd className={s.kbd} aria-hidden="true">
              {isMac() ? '⌘K' : 'Ctrl K'}
            </kbd>
          </Button>
          <Button
            ref={helpButtonRef}
            className={cx(s.clusterButton, s.help)}
            onPress={onHelpToggle}
            aria-expanded={helpOpen}
            {...(helpOpen && { 'aria-controls': 'help-panel' })}
          >
            <Icon name="help" size={18} />
            <span className={s.helpWord}>Help</span>
          </Button>
          {onFeedbackOpen !== undefined && (
            <FeedbackButton
              ref={feedbackRef}
              className={cx(s.clusterButton, s.feedback)}
              labelClassName={s.feedbackWord}
              onPress={() => {
                onFeedbackOpen(feedbackRef.current);
              }}
            />
          )}
          <BellButton bell={bell} homeHref={homeHref} />
          <AccountMenu
            me={me}
            canPreview={canPreview}
            settingsHref={settingsHref}
            onPreviewOpen={onPreviewOpen}
            onHelpOpen={onHelpOpen}
            onFeedbackOpen={onFeedbackOpen}
            onSignOut={onSignOut}
          />
        </div>
      </div>
    </header>
  );
}

function isMac(): boolean {
  return typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
}

/**
 * The bell's accessible name carries every number: unread notices, work waiting that no notice
 * announces, and getting-started steps ("Notifications, 2 new, 1 waiting on you, 1 to get started").
 */
export function bellLabel(unread: number, gettingStarted = 0, waiting = 0): string {
  const parts: string[] = [];
  if (unread > 0) parts.push(`${String(unread)} new`);
  if (waiting > 0) parts.push(`${String(waiting)} waiting on you`);
  if (gettingStarted > 0) parts.push(`${String(gettingStarted)} to get started`);
  return parts.length === 0 ? 'Notifications, nothing new' : `Notifications, ${parts.join(', ')}`;
}

function BellButton({ bell, homeHref }: { bell: BellState; homeHref: string }): ReactElement {
  const [open, setOpen] = useState(false);
  // "Mark all read" goes away once it's done; focus moves to the panel's title first, so it
  // never drops to the page behind the open panel (and Escape still closes it).
  const titleRef = useRef<HTMLHeadingElement>(null);
  const started = bell.gettingStarted.length;
  const waiting = bell.waiting.length;
  const lit = bell.unread + waiting + started > 0;
  const close = (): void => {
    setOpen(false);
    bell.onOpenChange?.(false);
  };
  const summary = [
    bell.unread > 0 ? `${String(bell.unread)} new` : null,
    waiting > 0 ? `${String(waiting)} waiting on you` : null,
    started > 0 ? `${String(started)} to get started` : null,
  ]
    .filter((p): p is string => p !== null)
    .join(' · ');
  return (
    <DialogTrigger
      isOpen={open}
      onOpenChange={(next) => {
        setOpen(next);
        bell.onOpenChange?.(next);
      }}
    >
      <Button className={s.bell} aria-label={bellLabel(bell.unread, started, waiting)}>
        <Icon name="bell" size={20} />
        {lit && <span className={s.bellDot} aria-hidden="true" />}
      </Button>
      {/* No nested Dialog: React Aria makes the popover itself the labelled dialog. */}
      <Popover
        placement="bottom end"
        offset={10}
        className={cx(s.popover, s.bellPopover)}
        aria-label="Notifications"
      >
        <div className={s.popoverRule} aria-hidden="true" />
        <div className={s.dialog}>
          <div className={s.bellHead}>
            <div className={s.bellHeadText}>
              <h2 ref={titleRef} tabIndex={-1} className={cx('disp', s.bellTitle)}>
                Notifications
              </h2>
              {summary !== '' && <span className={s.bellSummary}>{summary}</span>}
            </div>
            {bell.unread > 0 && (
              <Button
                className={s.bellTextButton}
                onPress={() => {
                  titleRef.current?.focus();
                  bell.onMarkAllRead();
                }}
              >
                Mark all read
              </Button>
            )}
          </div>
          <div className={s.bellBody}>
            {waiting > 0 && (
              <section aria-labelledby="bell-waiting" className={s.bellStarted}>
                <h3 id="bell-waiting" className={cx('eyebrow', s.bellSection)}>
                  Waiting on you
                </h3>
                <ul className={s.bellList}>
                  {bell.waiting.map((item) => (
                    <li key={item.id}>
                      <Link
                        href={item.href}
                        className={cx(s.bellItem, s.bellItemStarted)}
                        onPress={close}
                      >
                        <span className={s.bellItemTitle}>{item.title}</span>
                        {item.meta !== undefined && (
                          <span className={s.bellItemMeta}>{item.meta}</span>
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {started > 0 && (
              <section aria-labelledby="bell-started" className={s.bellStarted}>
                <h3 id="bell-started" className={cx('eyebrow', s.bellSection)}>
                  Getting started
                </h3>
                <ul className={s.bellList}>
                  {bell.gettingStarted.map((item) => (
                    <li key={item.id}>
                      <Link
                        href={item.href}
                        className={cx(s.bellItem, s.bellItemStarted)}
                        onPress={close}
                      >
                        <span className={s.bellItemTitle}>{item.title}</span>
                        {item.meta !== undefined && (
                          <span className={s.bellItemMeta}>{item.meta}</span>
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {bell.status === 'loading' ? (
              <p className={s.bellNote} role="status">
                Loading your notices…
              </p>
            ) : bell.status === 'error' ? (
              <p className={s.bellNote} role="alert">
                We couldn’t load your notices. Close the bell and open it again to retry.
              </p>
            ) : bell.notices.length === 0 ? (
              started + waiting === 0 && (
                <div className={s.bellEmpty}>
                  <EmptyState
                    variant="caught-up"
                    size="sm"
                    headingLevel={3}
                    headline="All caught up"
                    body="Nothing new. Notices about your awards and your work show up here; the work itself waits at the top of Home."
                  />
                </div>
              )
            ) : (
              <ul className={s.bellList} aria-label="Notices, newest first">
                {bell.notices.map((notice) => (
                  <li key={notice.id} className={cx(s.bellNotice, !notice.read && s.bellUnread)}>
                    <Link
                      href={notice.href}
                      className={s.bellItem}
                      onPress={() => {
                        if (!notice.read) bell.onOpenNotice(notice.id);
                        close();
                      }}
                    >
                      <span className={s.bellItemTitle}>
                        {!notice.read && <span className="visually-hidden">New: </span>}
                        {notice.title}
                      </span>
                      {notice.body !== undefined && (
                        <span className={s.bellItemBody}>{notice.body}</span>
                      )}
                      <span className={s.bellItemMeta}>{notice.when}</span>
                    </Link>
                    {!notice.read && <span className={s.unreadDot} aria-hidden="true" />}
                    <Button
                      className={s.bellDismiss}
                      aria-label={`Dismiss: ${notice.title}`}
                      onPress={() => {
                        bell.onDismiss(notice.id);
                      }}
                    >
                      <Icon name="close" size={16} />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className={s.bellFoot}>
            {bell.hasMore && (
              <Button
                className={s.bellTextButton}
                isDisabled={bell.loadingMore}
                onPress={bell.onLoadMore}
              >
                {bell.loadingMore ? 'Loading…' : 'Show older'}
              </Button>
            )}
            <ButtonLink href={homeHref} variant="ghost" size="sm" onPress={close}>
              Open Home
            </ButtonLink>
          </div>
        </div>
      </Popover>
    </DialogTrigger>
  );
}

function AccountMenu({
  me,
  canPreview,
  settingsHref,
  onPreviewOpen,
  onHelpOpen,
  onFeedbackOpen,
  onSignOut,
}: {
  me: Me;
  canPreview: boolean;
  settingsHref: string;
  onPreviewOpen: ((returnFocusTo: HTMLElement | null) => void) | undefined;
  onHelpOpen: () => void;
  onFeedbackOpen: ((returnFocusTo: HTMLElement | null) => void) | undefined;
  onSignOut: () => void;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const initials = me.initials ?? initialsOf(me.name);
  const tone = AVATAR_TONE[me.avatarTone ?? 'petrol'];

  const onAction = (key: Key): void => {
    setOpen(false);
    if (key === 'help') onHelpOpen();
    if (key === 'feedback') onFeedbackOpen?.(buttonRef.current);
    if (key === 'preview') onPreviewOpen?.(buttonRef.current);
    if (key === 'sign-out') onSignOut();
  };

  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen}>
      {/* Named by its visible text (label in name), then "account menu" for context. */}
      <Button ref={buttonRef} className={s.me} data-testid="account-menu">
        <span className={cx(s.avatar, tone)} aria-hidden="true">
          {initials}
        </span>
        <span className={s.who}>
          <span className={s.name}>
            {me.name}
            {me.pronouns !== undefined && <span className={s.pronouns}> ({me.pronouns})</span>}
          </span>
          {me.roleLine !== null && (
            <span className={s.roleLine}>
              <span className="visually-hidden">, </span>
              {me.roleLine}
            </span>
          )}
          <span className="visually-hidden">, account menu</span>
        </span>
        <Icon name={open ? 'chevronUp' : 'chevronDown'} size={14} className={s.caret} />
      </Button>
      <Popover placement="bottom end" offset={12} className={s.popover} aria-label="Your account">
        <div className={s.popoverRule} aria-hidden="true" />
        <div className={s.dialog}>
          <div className={s.menuHead}>
            <span className={cx(s.avatar, s.avatarLg, tone)} aria-hidden="true">
              {initials}
            </span>
            <span className={s.menuName}>
              <h2 className={s.menuNameText}>{me.name}</h2>
              {me.pronouns !== undefined && (
                <span className={s.menuPronouns}>
                  <span className="visually-hidden">Pronouns: </span>
                  {me.pronouns}
                </span>
              )}
              {me.email !== undefined && <span className={s.menuEmail}>{me.email}</span>}
            </span>
          </div>
          <div className={s.roles}>
            <h3 className={cx('eyebrow', s.sectionLabel)}>Your roles</h3>
            <ul className={s.roleList}>
              {me.roles.map((grant) => (
                <li key={`${grant.role}-${grant.scope ?? ''}`} className={s.roleItem}>
                  <StatusPill tone={grant.tone ?? 'info'} size="sm" label={grant.role} />
                  {grant.scope !== undefined && <span className={s.roleScope}>{grant.scope}</span>}
                </li>
              ))}
            </ul>
          </div>
          <Menu aria-label="Account" className={s.menu} onAction={onAction}>
            <MenuItem
              id="letter-handouts"
              href="/letters-to-hand-out"
              className={s.menuItem}
              textValue="Letters to hand out"
            >
              <Icon name="download" />
              Letters to hand out
            </MenuItem>
            <MenuItem
              id="settings"
              href={settingsHref}
              className={s.menuItem}
              textValue="My preferences"
            >
              <Icon name="sliders" />
              My preferences
            </MenuItem>
            {canPreview && onPreviewOpen !== undefined && (
              <MenuItem id="preview" className={s.menuItem} textValue="Preview as">
                <Icon name="eye" />
                <span className={s.menuItemText}>
                  <span>Preview as…</span>
                  <span className={s.menuItemSub}>Granted by a System Admin</span>
                </span>
              </MenuItem>
            )}
            <MenuItem id="help" className={s.menuItem} textValue="Help">
              <Icon name="help" />
              Help
            </MenuItem>
            {onFeedbackOpen !== undefined && (
              <MenuItem id="feedback" className={s.menuItem} textValue="Send feedback">
                <Icon name="message" />
                Send feedback
              </MenuItem>
            )}
            <MenuItem
              id="sign-out"
              className={cx(s.menuItem, s.signOut)}
              textValue="Sign out"
              data-testid="account-sign-out"
            >
              <Icon name="signOut" />
              Sign out
            </MenuItem>
          </Menu>
        </div>
      </Popover>
    </DialogTrigger>
  );
}
